import { useRef, useState, type ClipboardEvent, type FormEvent, type KeyboardEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { api } from '../api';
import { useI18n } from '../i18n';
import { useSession } from '../session';
import { Plate, Wordmark } from '../ui/chrome';
import { PhoneGlyph } from '../ui/glyphs';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Only paths on this site, so a crafted ?next= can't send someone elsewhere. */
export const safeNext = (next: string | null) => (next && /^\/[A-Za-z0-9/_?=&-]*$/.test(next) && !next.startsWith('//') ? next : '/shop');

/** Email code sign-in, the same account as the app (POST auth/email/request, then verify). */
export function LoginPage() {
  const { t } = useI18n();
  const session = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const boxes = useRef<(HTMLInputElement | null)[]>([]);

  if (session && !busy) return <Navigate to={next} replace />;

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!EMAIL_RE.test(email.trim())) return setError(t('login.badEmail'));
    setBusy(true);
    setError(null);
    try {
      await api.requestCode(email.trim());
      setSent(true);
      setDigits(Array(6).fill(''));
      setTimeout(() => boxes.current[0]?.focus(), 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('error.generic'));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (code: string) => {
    setBusy(true);
    setError(null);
    try {
      await api.verifyCode(email.trim(), code);
      navigate(next, { replace: true });
    } catch {
      setError(t('login.wrongCode'));
      setDigits(Array(6).fill(''));
      boxes.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  };

  const setDigit = (i: number, v: string) => {
    const d = v.replace(/\D/g, '').slice(-1);
    const nextDigits = digits.map((x, j) => (j === i ? d : x));
    setDigits(nextDigits);
    if (d && i < 5) boxes.current[i + 1]?.focus();
    if (nextDigits.every(Boolean)) void verify(nextDigits.join(''));
  };
  const onKey = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) boxes.current[i - 1]?.focus();
  };
  const onPaste = (e: ClipboardEvent) => {
    const code = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (code.length !== 6) return;
    e.preventDefault();
    setDigits(code.split(''));
    void verify(code);
  };

  return (
    <section className="auth">
      <div className="auth-bg" aria-hidden />
      <Link to="/" className="auth-logo" aria-label={t('nav.home')}>
        <Wordmark />
        <span className="tagline">{t('login.tagline')}</span>
      </Link>
      <div className="card auth-card">
        <Plate as="h1" large petal>
          {t('login.title')}
        </Plate>
        <form onSubmit={send} className="field" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="step">
            <i>1</i>
            {t('login.step1')}
          </div>
          <div>
            <label htmlFor="email">{t('login.email')}</label>
            <input id="email" className="input" type="email" autoComplete="email" inputMode="email" placeholder="you@example.com" value={email} disabled={sent} onChange={(e) => setEmail(e.target.value)} />
          </div>
          {sent ? (
            <button type="button" className="btn btn-ghost" style={{ alignSelf: 'flex-start', color: 'var(--sakura)', padding: '0 4px' }} onClick={() => (setSent(false), setError(null))}>
              {t('login.changeEmail')}
            </button>
          ) : (
            <button className="btn btn-pink btn-full" disabled={busy}>
              {t('login.send')}
            </button>
          )}
        </form>
        <div className="divider">✿</div>
        <div className="step">
          <i>2</i>
          {t('login.step2')}
        </div>
        <div className="code-boxes" onPaste={onPaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => void (boxes.current[i] = el)}
              className="input"
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              aria-label={`${t('login.step2')} ${i + 1}`}
              value={d}
              disabled={busy || !sent}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKey(i, e)}
            />
          ))}
        </div>
        {sent ? <p className="muted" style={{ fontWeight: 700, marginTop: -6 }}>{t('login.sentTo', { email: email.trim() })}</p> : null}
        <button className="btn btn-ink btn-full" disabled={busy || !sent || !digits.every(Boolean)} onClick={() => void verify(digits.join(''))}>
          {t('login.submit')}
        </button>
        <p className="row-center">
          {t('login.noCode')}
          <button className="btn btn-ghost" disabled={busy || !sent} onClick={() => void send()} style={{ color: 'var(--sakura)', fontSize: 18 }}>
            {t('login.resend')}
          </button>
        </p>
        {error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}
        <p className="notice same-account">
          <i>
            <PhoneGlyph />
          </i>
          {t('login.same')}
        </p>
      </div>
      <img className="auth-lineup" src="/art/lineup.webp" alt="" width={1400} height={1033} />
    </section>
  );
}
